import React, { useEffect, useState } from "react";
import { Plus, MapPin, Loader2, Power } from "lucide-react";

import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { useToast } from "src/components/ui/toast";
import { cn } from "src/utils/cn";
import { GeofenceMap } from "src/features/attendance/components/GeofenceMap";
import {
  fetchGeofences,
  createGeofence,
  updateGeofence,
  deleteGeofence,
} from "src/features/attendance/api/geofencesApi";

const DEFAULT_CENTER = { latitude: 19.076, longitude: 72.8777 }; // Mumbai

const blankDraft = () => ({
  name: "",
  address: "",
  latitude: DEFAULT_CENTER.latitude,
  longitude: DEFAULT_CENTER.longitude,
  radius_meters: 150,
  active: true,
});

export default function GeofencingPage() {
  const { toast } = useToast();
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const data = await fetchGeofences();
      setGeofences(data.geofences || []);
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Couldn't load locations." });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function startCreate() {
    setDraft(blankDraft());
  }

  function startEdit(g) {
    setDraft({ ...g });
  }

  function setCoords(lat, lng) {
    setDraft((d) => ({ ...d, latitude: lat, longitude: lng }));
  }

  async function save(e) {
    e.preventDefault();
    if (!draft.name.trim()) {
      toast({ variant: "destructive", title: "Validation", description: "Name is required." });
      return;
    }
    setSaving(true);
    const payload = {
      name: draft.name.trim(),
      address: draft.address,
      latitude: draft.latitude,
      longitude: draft.longitude,
      radius_meters: Number(draft.radius_meters),
      active: draft.active,
    };
    try {
      if (draft.id) {
        await updateGeofence(draft.id, payload);
      } else {
        await createGeofence(payload);
      }
      toast({ title: "Saved", description: "Attendance location saved." });
      setDraft(null);
      await load();
    } catch (error) {
      const msg = error.response?.data?.errors?.join(", ") || "Failed to save.";
      toast({ variant: "destructive", title: "Error", description: msg });
    } finally {
      setSaving(false);
    }
  }

  async function deactivate(g) {
    try {
      await deleteGeofence(g.id);
      toast({ title: "Deactivated", description: `${g.name} is now inactive.` });
      await load();
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to deactivate." });
    }
  }

  return (
    <div className="max-w-[1100px]">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Geofencing</h2>
          <p className="text-[13px] text-slate-500">
            Define the locations where team members can mark attendance.
          </p>
        </div>
        {!draft && (
          <Button onClick={startCreate}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add location
          </Button>
        )}
      </div>

      <div className="flex gap-6">
        {/* List */}
        <div className="w-[300px] shrink-0 space-y-2">
          {loading ? (
            <div className="flex items-center gap-2 p-4 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : geofences.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-[13px] text-slate-500">
              No locations yet. Add your first attendance location.
            </div>
          ) : (
            geofences.map((g) => (
              <Button
                key={g.id}
                variant="outline"
                onClick={() => startEdit(g)}
                className={cn(
                  "flex h-auto w-full items-start justify-start gap-2 rounded-lg border px-3 py-2.5 text-left font-normal transition-colors",
                  draft?.id === g.id
                    ? "border-[#4f39f6]/40 bg-[#ecebff] hover:bg-[#ecebff]"
                    : "border-slate-200 hover:bg-slate-50"
                )}
              >
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#4f39f6]" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-medium text-slate-900">
                    {g.name}
                  </span>
                  <span className="text-[12px] text-slate-500">
                    {g.radius_meters} m · {g.active ? "active" : "inactive"}
                  </span>
                </span>
              </Button>
            ))
          )}
        </div>

        {/* Editor */}
        <div className="min-w-0 flex-1">
          {draft ? (
            <form onSubmit={save} className="space-y-4">
              <GeofenceMap
                latitude={Number(draft.latitude)}
                longitude={Number(draft.longitude)}
                radiusMeters={Number(draft.radius_meters)}
                onChange={setCoords}
              />
              <p className="text-[12px] text-slate-500">
                Click the map or drag the pin to set the center.
              </p>

              <div>
                <label className="mb-1 block text-[12px] font-medium text-slate-600">Name</label>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="e.g. Andheri Site Office"
                />
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between text-[12px] text-slate-600">
                  <span className="font-medium">Radius</span>
                  <span className="text-slate-900">{draft.radius_meters} m</span>
                </div>
                <input
                  type="range"
                  min="20"
                  max="1000"
                  step="10"
                  value={draft.radius_meters}
                  onChange={(e) => setDraft({ ...draft, radius_meters: e.target.value })}
                  className="w-full accent-[#4f39f6]"
                />
              </div>

              <div className="flex items-center justify-between">
                {draft.id ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => deactivate(draft)}
                    className="text-rose-600"
                  >
                    <Power className="mr-1.5 h-4 w-4" />
                    Deactivate
                  </Button>
                ) : (
                  <span />
                )}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setDraft(null)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                    Save
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <div className="flex h-[280px] items-start justify-center rounded-lg text-[13px] text-slate-400">
              Select a location to edit, or add a new one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
